/**
 * SharePage — Externe Freigabe-Ansicht (kein Login nötig)
 * Erreichbar unter /share/:token
 *
 * Kunden können:
 *  - alle Posts des Monatsplans sehen
 *  - Posts per Button freigeben (→ status "approved")
 *  - einen kurzen Kommentar hinterlassen
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  CheckCircle, Loader2, MessageSquare, Calendar, Clock,
  AlertCircle, ChevronDown, ChevronUp, Instagram, Facebook,
  Linkedin, Globe, Send
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Hilfsdaten ────────────────────────────────────────────────────────────────

const PLATFORM_CONFIG = {
  instagram:       { icon: <Instagram size={13} />, label: "Instagram",        cls: "bg-gradient-to-r from-purple-500 to-pink-500 text-white" },
  facebook:        { icon: <Facebook  size={13} />, label: "Facebook",         cls: "bg-blue-600 text-white" },
  linkedin:        { icon: <Linkedin  size={13} />, label: "LinkedIn",         cls: "bg-sky-700 text-white" },
  tiktok:          { icon: <Globe     size={13} />, label: "TikTok",           cls: "bg-stone-900 text-white" },
  google_business: { icon: <Globe     size={13} />, label: "Google Business",  cls: "bg-orange-500 text-white" },
};

const STATUS_STYLE = {
  draft:     { cls: "bg-stone-100 text-stone-500",  label: "Entwurf" },
  review:    { cls: "bg-amber-100 text-amber-700",  label: "Zur Freigabe",  pulse: true },
  approved:  { cls: "bg-green-100 text-green-700",  label: "Freigegeben" },
  done:      { cls: "bg-stone-100 text-stone-400",  label: "Erledigt" },
  published: { cls: "bg-green-100 text-green-700",  label: "Veröffentlicht" },
  ready:     { cls: "bg-blue-100 text-blue-700",    label: "Bereit" },
  scheduled: { cls: "bg-sky-100 text-sky-700",      label: "Geplant" },
};

const FORMAT_ICON = { post: "📝", carousel: "🖼️", video: "🎬" };

function formatDateDE(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("de-DE", {
      weekday: "short", day: "numeric", month: "long",
    });
  } catch { return dateStr; }
}

function formatExpiresDE(isoStr) {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleDateString("de-DE", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
}

// ── PostCard ─────────────────────────────────────────────────────────────────

function PostCard({ post, token, onApproved, onNoted }) {
  const [approving, setApproving]       = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText]         = useState("");
  const [sendingNote, setSendingNote]   = useState(false);
  const [localStatus, setLocalStatus]   = useState(post.status);
  const [localNotes, setLocalNotes]     = useState(post.notes || "");

  const canApprove  = ["review", "draft"].includes(localStatus);
  const statusStyle = STATUS_STYLE[localStatus] || STATUS_STYLE.draft;
  const platCfg     = PLATFORM_CONFIG[post.platform] || { icon: <Globe size={13} />, label: post.platform, cls: "bg-stone-500 text-white" };

  // FREIGABE-01: Volltext für die Vorsicht des Freigebenden. `core_content` ist das
  // ausformulierte Feld, `text_content` die ältere Fassung — es gibt Bestandsposts
  // mit dem einen und mit dem anderen, deshalb beide in dieser Reihenfolge.
  const bodyText     = (post.core_content || post.text_content || "").trim();
  const hashtagList  = Array.isArray(post.hashtags) ? post.hashtags.filter(Boolean) : [];
  // Diese Seite ist ÖFFENTLICH erreichbar. Bildquellen kommen aus der Datenbank und
  // werden deshalb auf http(s) bzw. app-eigene Pfade begrenzt — kein data:/blob:.
  const previewImages = [post.image_url, ...(Array.isArray(post.card_urls) ? post.card_urls : [])]
    .filter((u) => typeof u === "string" && (/^https?:\/\//i.test(u) || u.startsWith("/")))
    .slice(0, 10);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(`${API}/share/${token}/approve/${post.id}`);
      setLocalStatus("approved");
      toast.success("Freigegeben! ✓");
      onApproved?.(post.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Freigeben. Bitte erneut versuchen.");
    } finally {
      setApproving(false);
    }
  };

  const handleSendNote = async () => {
    if (!noteText.trim()) return;
    setSendingNote(true);
    try {
      const res = await axios.post(`${API}/share/${token}/note/${post.id}`, { note: noteText });
      const combined = res.data.notes || noteText;
      setLocalNotes(combined);
      setNoteText("");
      setShowNoteForm(false);
      toast.success("Kommentar gespeichert");
      onNoted?.(post.id, combined);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kommentar konnte nicht gespeichert werden.");
    } finally {
      setSendingNote(false);
    }
  };

  return (
    <div className={`bg-white rounded-2xl border-2 p-5 transition-all duration-200 ${
      localStatus === "approved" ? "border-green-200 shadow-green-50 shadow-md" :
      localStatus === "review"   ? "border-amber-200" :
      "border-stone-100"
    }`}>

      {/* ── Meta-Zeile ── */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center flex-wrap gap-2 text-sm text-stone-500">
          <span className="flex items-center gap-1 font-medium text-stone-700">
            <Calendar size={13} className="text-stone-400" />
            {formatDateDE(post.date)}
          </span>
          {post.time && (
            <span className="flex items-center gap-1 text-stone-400">
              <Clock size={12} /> {post.time}
            </span>
          )}
          {/* Plattform-Badge */}
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${platCfg.cls}`}>
            {platCfg.icon} {platCfg.label}
          </span>
          {/* Format */}
          <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">
            {FORMAT_ICON[post.format_key] || "📝"} {post.format}
          </span>
          {/* Pillar */}
          {post.pillar && (
            <span className="text-xs bg-stone-50 text-stone-400 px-2 py-0.5 rounded-full border border-stone-100">
              {post.pillar}
            </span>
          )}
        </div>
        {/* Status-Badge */}
        <span className={`flex-shrink-0 text-xs px-3 py-1 rounded-full font-semibold ${statusStyle.cls}`}>
          {localStatus === "review" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5 animate-pulse" />}
          {localStatus === "approved" && "✓ "}
          {statusStyle.label}
        </span>
      </div>

      {/* ── Hook / Inhalt ──
          FREIGABE-01 (16.08.2026): Hier stand nur die Hook-Zeile. Wer freigibt,
          muss sehen, was er freigibt — Volltext, Aufruf zum Handeln, Hashtags
          und Bild. Das Backend liefert die Felder seit derselben Reparatur mit. */}
      <p className="text-stone-900 text-base leading-snug mb-3 font-medium">{post.hook_full || post.hook}</p>

      {bodyText && (
        <p className="text-stone-700 text-sm leading-relaxed mb-3 whitespace-pre-line">{bodyText}</p>
      )}

      {post.cta && (
        <p className="text-stone-800 text-sm leading-relaxed mb-3 font-medium">{post.cta}</p>
      )}

      {hashtagList.length > 0 && (
        <p className="text-sm text-blue-600 mb-3 break-words">
          {hashtagList.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
        </p>
      )}

      {previewImages.length > 0 && (
        <div className="mb-4 flex gap-2 overflow-x-auto">
          {previewImages.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={post.card_alts?.[i] || `Bild ${i + 1} zum Beitrag`}
              loading="lazy"
              className="h-40 w-auto rounded-xl border border-stone-200 object-cover flex-shrink-0"
            />
          ))}
        </div>
      )}

      {!bodyText && !post.cta && hashtagList.length === 0 && previewImages.length === 0 && (
        <p className="text-xs text-stone-400 mb-4 italic">
          Für diesen Beitrag ist bisher nur die Überschrift erstellt — der ausformulierte
          Text folgt noch.
        </p>
      )}

      {/* ── Bestehende Notizen ── */}
      {localNotes && (
        <div className="mb-4 p-3 bg-stone-50 rounded-xl text-sm text-stone-600 border border-stone-100">
          <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1.5">Notizen</p>
          <p className="whitespace-pre-line leading-relaxed">{localNotes}</p>
        </div>
      )}

      {/* ── Aktionen ── */}
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Freigeben-Button */}
        {canApprove && (
          <button
            onClick={handleApprove}
            disabled={approving}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-xl font-semibold text-sm hover:bg-green-600 active:bg-green-700 disabled:opacity-50 transition-colors shadow-sm shadow-green-200"
          >
            {approving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
            Freigeben
          </button>
        )}

        {/* Freigegeben-Bestätigung */}
        {localStatus === "approved" && (
          <span className="flex items-center gap-1.5 text-green-700 font-semibold text-sm">
            <CheckCircle size={15} /> Freigegeben ✓
          </span>
        )}

        {/* Kommentar-Toggle */}
        <button
          onClick={() => { setShowNoteForm(!showNoteForm); if (showNoteForm) setNoteText(""); }}
          className="flex items-center gap-1.5 px-3.5 py-2 text-sm text-stone-500 bg-stone-50 rounded-xl hover:bg-stone-100 border border-stone-200 transition-colors"
        >
          <MessageSquare size={13} />
          Kommentar
          {showNoteForm ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </div>

      {/* ── Kommentar-Formular ── */}
      {showNoteForm && (
        <div className="mt-3 flex gap-2 items-start">
          <textarea
            rows={2}
            placeholder="Dein Kommentar, Feedback oder Änderungswunsch…"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSendNote(); }}
            className="flex-1 text-sm border border-stone-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-stone-300 resize-none bg-white"
            autoFocus
          />
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button
              onClick={handleSendNote}
              disabled={sendingNote || !noteText.trim()}
              className="flex items-center gap-1 px-3 py-2 bg-stone-800 text-white rounded-xl text-xs font-semibold disabled:opacity-40 hover:bg-stone-900 transition-colors"
            >
              {sendingNote ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              Senden
            </button>
            <button
              onClick={() => { setShowNoteForm(false); setNoteText(""); }}
              className="px-3 py-1.5 text-stone-400 text-xs hover:text-stone-600 transition-colors"
            >
              Abbruch
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SharePage ─────────────────────────────────────────────────────────────────

const SharePage = () => {
  const { token }  = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/share/${token}`);
        if (res.data.valid) {
          setData(res.data);
        } else {
          setError(res.data.error || "Dieser Link ist ungültig.");
        }
      } catch {
        setError("Verbindungsfehler — bitte versuche es später erneut.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  // Posts nach Datum gruppieren
  const postsByDate = (data?.posts || []).reduce((acc, p) => {
    const d = p.date || "unbekannt";
    if (!acc[d]) acc[d] = [];
    acc[d].push(p);
    return acc;
  }, {});

  const pendingCount  = (data?.posts || []).filter(p => p.status === "review").length;
  const approvedCount = (data?.posts || []).filter(p => p.status === "approved").length;

  // ── Loading-State ──
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-stone-400" />
      </div>
    );
  }

  // ── Fehler / abgelaufener Link ──
  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <Toaster position="top-center" richColors />
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertCircle size={28} className="text-stone-400" />
          </div>
          <h1 className="text-xl font-bold text-stone-800 mb-2">Link nicht mehr gültig</h1>
          <p className="text-stone-500 text-sm leading-relaxed">{error}</p>
          <p className="text-stone-400 text-xs mt-4">
            Bitte wende dich an die Person, die dir diesen Link geschickt hat.
          </p>
        </div>
      </div>
    );
  }

  // ── Hauptansicht ──
  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-center" richColors />

      {/* ── Sticky Header ── */}
      <div className="bg-white border-b border-stone-200 px-4 py-4 sticky top-0 z-20 shadow-sm">
        <div className="max-w-2xl mx-auto flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-bold text-stone-900 text-lg leading-tight">{data.title}</h1>
            {data.label && (
              <p className="text-xs text-stone-400 mt-0.5">{data.label}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {pendingCount > 0 && (
                <span className="bg-amber-100 text-amber-700 px-2.5 py-0.5 rounded-full text-xs font-bold">
                  {pendingCount} zur Freigabe
                </span>
              )}
              {approvedCount > 0 && (
                <span className="bg-green-100 text-green-700 px-2.5 py-0.5 rounded-full text-xs font-bold">
                  {approvedCount} freigegeben
                </span>
              )}
            </div>
            {data.expires_at && (
              <p className="text-[10px] text-stone-300">
                Gültig bis {formatExpiresDE(data.expires_at)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Hinweis-Banner wenn alles freigegeben ── */}
      {pendingCount === 0 && approvedCount === data.posts.length && data.posts.length > 0 && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-3 text-center">
          <p className="text-sm text-green-700 font-medium">
            ✓ Alle Posts wurden freigegeben. Danke!
          </p>
        </div>
      )}

      {/* ── Posts ── */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
        {Object.entries(postsByDate).map(([date, posts]) => (
          <div key={date}>
            <h2 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3 px-1">
              {formatDateDE(date)}
            </h2>
            <div className="space-y-3">
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  token={token}
                  onApproved={(id) => setData(prev => ({
                    ...prev,
                    posts: prev.posts.map(p =>
                      p.id === id ? { ...p, status: "approved", status_label: "Freigegeben" } : p
                    ),
                  }))}
                  onNoted={(id, notes) => setData(prev => ({
                    ...prev,
                    posts: prev.posts.map(p => p.id === id ? { ...p, notes } : p),
                  }))}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Leer-State */}
        {data.posts.length === 0 && (
          <div className="text-center py-16 text-stone-400">
            <Calendar size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Für diesen Monat sind noch keine Posts geplant.</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-4 pb-10">
          <p className="text-[11px] text-stone-300">Erstellt mit KI Social Media Manager</p>
        </div>
      </div>
    </div>
  );
};

export default SharePage;
